import { isNumber } from '@vueuse/core'
import type { ReactiveParam } from '@/utils/reactive-param'
import type { UnwrapNestedRefs } from 'vue'

export type Values<T extends Value | Import = Value> = UnwrapNestedRefs<
    ReturnType<typeof useValues<T>>
>

export type ChildrenCountryValue = {
    dimension: Dimension
    value: Value
}

export type ChildrenCountryValues = {
    country: string
    values: ChildrenCountryValue[]
}

export type RegionalValues = {
    id: number
    iso: string
    name: string
    countries: Country['iso'][]
    color: string
    value: number
    score: number
    total: number
}

/**
 * Use values for the given dimension.
 */
export function useValues<T extends Value | Import = Value>(
    dimensionRef?: ReactiveParam<Dimension | undefined>,
    allCountries?: boolean
) {
    /**
     * Get the chart config.
     */
    const { config } = useParams()

    /**
     * Get the framework.
     */
    const { isImportingRisk, isPrevalence } = useFramework()

    /**
     * Get the regions and regional average method.
     */
    const { byRegion, countries, getIncome, isG20, getName } = useCountries()

    /**
     * Use meta data.
     */
    const { meta } = useMeta()

    /**
     * Store the data.
     */
    const data = ref<T[]>()

    /**
     * Loading state.
     */
    const loading = ref(false)

    /**
     * Get the current dimension.
     */
    const dimension = reactiveParam(dimensionRef)

    /**
     * Whether the values are absolute.
     */
    const isAbsolute = computed(() => {
        return (
            isPrevalence(unref(dimension)) &&
            config.prevalenceUnit === 'absolute'
        )
    })

    /**
     * Value|Import key.
     */
    const valueKey = computed<'value' | 'score'>(() => {
        return unref(isAbsolute)
            ? config.showRegions && config.dimension === 'p'
                ? ('total' as any)
                : 'value'
            : 'score'
    })

    /**
     * Get the countries params.
     */
    function getCountriesParams(isos?: string[]) {
        if (allCountries || !config.country)
            return unref(countries).map(({ iso }) => iso)

        return (isos ?? [config.country]).filter(Boolean)
    }

    /**
     * Fetch the data.
     */
    async function fetchData<D extends Dimension | Dimension[]>(
        dimensionParam: D,
        countries?: string[]
    ) {
        const dimensions: Dimension[] = (
            Array.isArray(dimensionParam) ? dimensionParam : [dimensionParam]
        ).filter(Boolean)

        if (!dimensions.length) return []

        const dimensionId = dimensions.map(({ id }) => id)

        try {
            loading.value = true

            const url = isImportingRisk(dimensions[0]) ? 'imports' : 'values'

            const data = await apiFetch<T[]>(url, {
                dimensionId,
                iso: getCountriesParams(countries)
            })

            return data
        } catch (e) {
            // console.error(
            //     `Could not fetch data for dimensions: ${dimensionId.join(', ')}`
            // )
            return []
        } finally {
            loading.value = false
        }
    }

    /**
     * Fetch the data for the current dimension.
     */
    async function fetch() {
        const dim = unref(dimension)

        data.value = undefined

        if (!dim) return

        data.value = await fetchData(dim)
    }

    /**
     * Get the value formatter.
     */
    function getValueFormatter(
        absolute = unref(isAbsolute),
        dimensionId = config.dimension
    ) {
        return isImportingRisk(dimensionId)
            ? importsValueFomatter()
            : absolute
            ? prevalenceAbsoluteFormatter()
            : defaultFormatter()
    }

    /**
     * Value|Import formatter.
     */
    const formatter = computed(getValueFormatter)

    /**
     * Non-null data.
     */
    const nonNull = computed(() => {
        const values = (unref(data) ?? []) as Value[]

        return values.filter(({ value }) => isNumber(value))
    })

    /**
     * Filter the data.
     */
    function filter(
        values = unref(data) as Value[],
        countries = config.compareCountries
    ) {
        if (!values) return []

        if (!countries) return unref(nonNull)

        return unref(nonNull).filter(item => {
            return countries.includes(item.iso)
        })
    }

    /**
     * Filtered data.
     */
    const filtered = computed(filter)

    /**
     * Limit the data based on the chart config.
     */
    function limit(
        values = unref(filtered),
        filter = config.filter,
        key = unref(valueKey)
    ) {
        if (!values) return []

        if (filter === 'all') return values

        if (filter === 'g20') {
            return values.filter(item => isG20(item.iso))
        }

        if (filter.match(/.*-income$/)) {
            const income = filter.replace('-income', '')

            return values.filter(item => getIncome(item.iso) === income)
        }

        const [_, direction, limit] =
            filter.match(/(top|bottom)-([0-9]+)/) ?? []

        if (direction === 'bottom') {
            values.sort((a, b) => a[key] - b[key])
        } else if (direction === 'top') {
            values.sort((a, b) => b[key] - a[key])
        }

        return values.slice(0, Number(limit))
    }

    /**
     * Limited data.
     */
    const limited = computed(limit)

    /**
     * Sort the data based on the chart config.
     */
    function sort(
        values = unref(limited),
        sort = config.sort,
        key = unref(valueKey)
    ) {
        if (!values) return []

        if (sort === 'asc') {
            values.sort((a, b) => {
                if (key === 'score' && a[key] === b[key]) {
                    return a.value - b.value
                }

                return a[key] - b[key]
            })
        } else if (sort === 'desc') {
            values.sort((a, b) => {
                if (key === 'score' && a[key] === b[key]) {
                    return b.value - a.value
                }

                return b[key] - a[key]
            })
        } else if (sort === 'alphabetical') {
            values.sort((a, b) => {
                return getName(a.iso)?.localeCompare(getName(b.iso) || '') ?? 0
            })
        }

        return values
    }

    /**
     * Sorted data.
     */
    const sorted = computed(sort)

    /**
     * Calculate the total and list of a given dataset.
     */
    function calcRegionalData<T>(
        data: T[],
        regionId: number,
        valueKey = 'value',
        countryKey = 'iso'
    ) {
        const codes = unref(byRegion).get(regionId) ?? []

        const list = data.filter(item => {
            const code = String(item[countryKey as keyof T])
            return codes.includes(code) && isNumber(item[valueKey as keyof T])
        })

        const total = list.reduce((total, item) => {
            return total + Number(item[valueKey as keyof T])
        }, 0)

        const avg = total / list.length

        return { total, list, avg }
    }

    /**
     * Calculate the regional averages.
     */
    function getRegionalData(values = unref(data)) {
        if (!values) return []

        return meta.regions.map(region => {
            const { total, avg: avgValue } = calcRegionalData(
                values,
                region.id,
                'value'
            )

            const scoreKey =
                dimension.value?.id === 'p'
                    ? 'prevalence'
                    : dimension.value?.id === 'r'
                    ? 'gov_response'
                    : dimension.value?.id === 'v'
                    ? 'vulnerability'
                    : null

            const score = scoreKey
                ? region['2023']?.[scoreKey] ?? 0
                : calcRegionalData(values, region.id, 'score').avg

            return {
                id: region.id,
                iso: region.name,
                name: region.name,
                color: region.color,
                countries: unref(byRegion).get(region.id) ?? [],
                score: score,
                value: avgValue,
                total
            } satisfies RegionalValues
        })
    }

    /**
     * Regional data.
     */
    const regionalData = computed(getRegionalData)

    /**
     * Filtered regional data.
     */
    function filterRegional(
        values = unref(regionalData),
        countries = config.compareCountries
    ) {
        if (!countries?.length) return values.slice()

        return values.slice().filter(item => {
            return countries.some(iso => item.countries.includes(iso))
        })
    }

    /**
     * Filtered regional data.
     */
    const regionalFiltered = computed(filterRegional)

    /**
     * Limited regional data.
     */
    const regionalLimited = computed(() => {
        return limit(unref(regionalFiltered) as unknown as Value[])
    })

    /**
     * Sorted regional data.
     */
    const regionalSorted = computed(() => {
        return sort(unref(regionalLimited))
    })

    /**
     * List the regional averages to display.
     */
    const regionalAverages = computed(() => {
        return unref(regionalData).filter(({ id }) =>
            config.compareRegions?.includes(id)
        )
    })

    /**
     * Get the data for a specific country.
     */
    function getCountryData(countries?: Country['iso'][]): T[]
    function getCountryData(country: Country['iso']): T | undefined
    function getCountryData(countries?: Country['iso'] | Country['iso'][]) {
        if (!countries) return

        if (!Array.isArray(countries)) {
            return unref(data)?.find(value =>
                'iso' in value
                    ? value.iso === countries
                    : value.country === countries
            )
        }

        return unref(data)?.filter(value => {
            return countries?.includes(
                'iso' in value ? value.iso : value.country
            )
        })
    }

    /**
     * Value|Import for the current country.
     */
    const countryData = computed(() => {
        return getCountryData(config.country)
    })

    /**
     * Value|Import for the first comparison country.
     */
    const compareCountryData = computed(() => {
        return getCountryData(config.compareCountry)
    })

    /**
     * Value|Import for the comparison countries.
     */
    const compareCountriesData = computed(() => {
        return getCountryData(config.compareCountries)
    })

    /**
     * Fetch the data on `dimension`change.
     */
    watch(dimension, fetch, { immediate: true })

    return {
        data,
        dimension,
        fetchData,
        isAbsolute,
        valueKey,
        formatter,
        loading,
        nonNull,
        sort,
        filter,
        limit,
        sorted,
        limited,
        filtered,
        regionalData,
        regionalAverages,
        regionalFiltered,
        regionalLimited,
        regionalSorted,
        calcRegionalData,
        getRegionalData,
        countryData,
        compareCountriesData,
        compareCountryData,
        getValueFormatter
    }
}
